import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, Plus, Trash2, ShieldAlert } from 'lucide-react';
import { useMySchoolMembership, useSchoolStaff, useManageSchoolMembership } from '@/hooks/useSchoolApi';

export default function StaffManager() {
  const { data: membership, isLoading: membershipLoading } = useMySchoolMembership();
  const schoolId = (membership as any)?.schools?.id || membership?.school_id;
  // FIX-R40-MED-1: route-level role gate. The SchoolPortal nav already
  // hides this entry from instructors, but the /school/staff URL was
  // reachable directly. School admins are the only role allowed to
  // view / manage staff (matches manage-school-membership edge function
  // role gate at index.ts:42). Render a forbidden message rather than
  // exposing the staff list to instructors who guess the URL.
  const isSchoolAdmin = membership?.role === 'school_admin';
  const { data: staff, isLoading } = useSchoolStaff(isSchoolAdmin ? schoolId : undefined);
  const manageMembership = useManageSchoolMembership();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState('');

  // For now, invite by user ID (email lookup would require additional RPC)
  const handleInvite = () => {
    // This is a simplified version — in production you'd look up the user by email
    manageMembership.mutate(
      { action: 'add_member', schoolId, userId: email.trim(), role: 'instructor' },
      { onSuccess: () => { setDialogOpen(false); setEmail(''); } }
    );
  };

  const handleRemove = (userId: string) => {
    manageMembership.mutate({ action: 'remove_member', schoolId, userId });
  };

  if (membershipLoading || isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  // FIX-R40-MED-1: explicit instructor block at the page level.
  if (!isSchoolAdmin) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold tracking-tight">Staff</h2>
        <Card>
          <CardContent className="flex flex-col items-center text-center gap-3 py-12">
            <ShieldAlert className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground max-w-sm">
              Staff management is available to school admins only. Ask your
              school admin if you need access changes.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Staff</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Instructor</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Instructor</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label>User ID (UUID)</Label>
                {/* FIX-R38-MED: clarify that this expects a UUID, not an email,
                    until the email-lookup RPC ships. The state variable is
                    still named `email` for now to avoid a wider rename. */}
                <Input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
                <p className="text-xs text-muted-foreground">
                  Paste the instructor's user UUID. Email-based lookup is on the roadmap.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleInvite} disabled={!email.trim() || manageMembership.isPending}>
                {manageMembership.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!staff?.length ? (
        <p className="text-sm text-muted-foreground text-center py-8">No staff members.</p>
      ) : (
        <div className="space-y-3">
          {staff.map((member: any) => (
            <Card key={member.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">{member.profiles?.full_name || member.profiles?.email || member.user_id.slice(0, 8) + '…'}</p>
                  <Badge variant={member.role === 'school_admin' ? 'default' : 'secondary'} className="text-xs">
                    {member.role === 'school_admin' ? 'School Admin' : 'Instructor'}
                  </Badge>
                </div>
                {member.role === 'instructor' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    disabled={manageMembership.isPending}
                    onClick={() => handleRemove(member.user_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
